import  { useState, useEffect, useRef } from "react";
import {
  Link,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import LoginForm from "../../components/auth/LoginForm";
import SignupForm from "../../components/auth/RegisterForm";
import VerificationModal from "../../components/auth/VerificationModal";
import authBg from "../../assets/auth-bg.png";
import nusi from "../../assets/nusi.svg";
import powered from "../../assets/powered.svg";
import "../../styles/AuthSwitcher.animations.css";
const dgShippingLogo = "https://data.sagarmeinyog.com//DGMA_logo.png";

const navItems = [
  {
    name: "Login",
    href: "/",
  },
  {
    name: "Sign up",
    href: "#signup",
  },
  {
    name: "About",
    href: "#about",
  },
  {
    name: "FAQ",
    href: "#faq",
  },
];

const AuthSwitcher = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSignup, setIsSignup] = useState(false);
  const [reduceWidth, setReduceWidth] = useState(true);
  const [loginFade, setLoginFade] = useState("in"); // 'in', 'out', or 'hidden'
  const [signupFade, setSignupFade] = useState("hidden"); // 'in', 'out', or 'hidden'
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationToken, setVerificationToken] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const transitionTimeout = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuRef]);

  // Handle successful signup
  const handleSignupSuccess = () => {
    setReduceWidth(false);
  };

  // Check for verification token in URL
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      setVerificationToken(token);
      setShowVerificationModal(true);
      // Clean up the URL by removing the token parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("token");
      setSearchParams(newSearchParams);
    }
  }, [searchParams, setSearchParams]);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const token = searchParams.get("token");
      if (token) {
        setVerificationToken(token);
        setShowVerificationModal(true);
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete("token");
        setSearchParams(newSearchParams);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [searchParams, setSearchParams]);

  // Handle verification modal close
  const handleVerificationClose = () => {
    setShowVerificationModal(false);
    setVerificationToken(null);
  };

  // Listen for custom event to switch forms from child components
  useEffect(() => {
    const handler = (e) => {
      if (e.detail && e.detail.to === "signup" && !isSignup) {
        // Fade out login, then show signup
        setReduceWidth(false);
        setLoginFade("out");
        transitionTimeout.current = setTimeout(() => {
          setLoginFade("hidden");
          setSignupFade("in");
          setIsSignup(true);
        }, 300);
      }
      if (e.detail && e.detail.to === "login" && isSignup) {
        // Fade out signup, then show login
        setReduceWidth(true);
        setSignupFade("out");
        transitionTimeout.current = setTimeout(() => {
          setSignupFade("hidden");
          setLoginFade("in");
          setIsSignup(false);
        }, 300);
      }
    };
    window.addEventListener("switch-auth", handler);
    return () => {
      window.removeEventListener("switch-auth", handler);
      if (transitionTimeout.current) clearTimeout(transitionTimeout.current);
    };
  }, [isSignup]);

  // Handle navigation items click
  const handleNavItemClick = (href) => {
    if (href === "#signup") {
      if (!isSignup) {
        setReduceWidth(false);
        setLoginFade("out");
        transitionTimeout.current = setTimeout(() => {
          setLoginFade("hidden");
          setSignupFade("in");
          setIsSignup(true);
        }, 300);
      }
    } else if (href === "/") {
      if (isSignup) {
        setReduceWidth(true);
        setSignupFade("out");
        transitionTimeout.current = setTimeout(() => {
          setSignupFade("hidden");
          setLoginFade("in");
          setIsSignup(false);
        }, 300);
      }
    } else if (href === "#faq") {
      // Smooth scroll to FAQ section with delay
      setTimeout(() => {
        const faqSection = document.getElementById("faq");
        if (faqSection) {
          faqSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 150);
    } else if (href === "#about") {
      // Smooth scroll to About section with delay
      setTimeout(() => {
        const aboutSection = document.getElementById("about");
        if (aboutSection) {
          aboutSection.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 150);
    }
  };

  return (
    <div className="relative min-h-screen w-full bg-[#f6fafc]">
      {/* Header */}
      <div className="relative z-50">
      <div className="relative z-10 mx-auto flex min-h-[125px] w-full max-w-[calc(100vw-4rem)] items-start justify-between px-4 py-4 sm:items-center sm:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="flex items-center">
                <img src={nusi} alt="NUSI" className="h-12 sm:h-16" />
              </div>
              <img
                src={dgShippingLogo}
                alt="Directorate General of Maritime Administration"
                className="h-12 w-auto object-contain sm:h-16"
              />
            </div>
            <div className="ml-0 sm:ml-2">
              <div className="text-xs sm:text-sm font-semibold text-gray-700">
                भारत सरकार
              </div>
              <div className="text-sm sm:text-xl font-bold text-gray-900">
                Directorate General of Maritime Administration
              </div>
              <div className="text-xs sm:text-sm text-gray-600">
                Ministry of Ports, Shipping and Waterways
              </div>
              <div className="text-[10px] sm:text-xs text-gray-500">
                Government of India
              </div>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center justify-end gap-2">
            {navItems.map((item) => {
              let isActive =
                (item.href === location.pathname && !isSignup) ||
                (item.href === "#signup" && isSignup) ||
                (item.href === "/" && !isSignup);
              const className = isActive
                ? "bg-[var(--primary-font)] text-white"
                : "hover:bg-[var(--primary-font)] hover:text-white text-[var(--default-font)]";

              if (item.href.startsWith("#") || item.href === "/") {
                return (
                  <button
                    key={item.name}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavItemClick(item.href);
                    }}
                    className={`${className} px-3 sm:px-5 py-2 rounded-full transition-all text-sm sm:text-base ${!isActive ? "bg-transparent" : ""} border-none cursor-pointer`}
                  >
                    {item.name}
                  </button>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${className} px-3 sm:px-5 py-2 rounded-full transition-all text-sm sm:text-base`}
                >
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="block h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <div
          ref={mobileMenuRef}
          className={`lg:hidden absolute top-full left-0 right-0 bg-white border-2 !border-gray-200 mx-2 rounded-xl shadow-lg transition-all duration-300 ease-in-out transform ${
            mobileMenuOpen
              ? "translate-y-0 opacity-100"
              : "-translate-y-4 opacity-0 pointer-events-none"
          }`}
        >
          <div className="px-2 pt-2 pb-4 space-y-1">
            {navItems.map((item) => {
              let isActive =
              (item.href === location.pathname && !isSignup) ||
                (item.href === "#signup" && isSignup) ||
                (item.href === "/" && !isSignup);
              const className = isActive
                ? "bg-[var(--primary-font)] text-white"
                : "hover:bg-[var(--primary-font)] hover:text-white text-[var(--default-font)]";

              if (item.href.startsWith("#") || item.href === "/") {
                return (
                  <button
                    key={item.name}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavItemClick(item.href);
                      setMobileMenuOpen(false);
                    }}
                    className={`${className} w-full text-left block px-3 py-2 rounded-md text-base font-medium ${!isActive ? "bg-transparent" : ""} border-none cursor-pointer`}
                  >
                    {item.name}
                  </button>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`${className} block px-3 py-2 rounded-md text-base font-medium`}
                >
                  {item.name}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Animated Auth Frame */}
      <div className="relative z-10 mx-auto mt-2 flex w-full xl:max-w-[1640px] flex-col items-center justify-center overflow-hidden rounded-3xl px-4 pb-20 sm:px-6 lg:mt-4 lg:h-[800px] lg:flex-row lg:items-end lg:justify-between lg:px-12 lg:pb-10">
        <div
          className={`absolute inset-0 h-full w-full rounded-3xl bg-cover bg-center transition-all duration-500 ${!reduceWidth ? "lg:w-[60%] 2xl:w-[65%] lg:right-auto" : "lg:w-full"}`}
          style={{ backgroundImage: `url(${authBg})` }}
        />

        {/* Left side : content with bg image */}
        <div className="flex h-full w-full flex-col items-center justify-between lg:items-start lg:justify-start">
          {/* Powered by logo - positioned in top left of the background area */}
          <div className="">
            <img
              src={powered}
              alt="Powered by NUSI"
              className="h-16 opacity-90"
            />
          </div>

          <div className="z-10 font-helvetica flex-1 h-full flex flex-col justify-end items-start pb-4 lg:pb-16">
            <div className="mb-8 hidden lg:block">
              <a
                href="https://data.sagarmeinyog.com/uploads/general/pdfs/Home%20Page/SMY%20Curriculum.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                <button className="glass text-white px-6 py-3 rounded-lg border border-white/30 text-sm hover:bg-white/30 transition-colors flex items-center gap-2">
                  E-learning Curriculum
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </button>
              </a>
            </div>
            <div className="text-white">
              <h1 className="xl:text-6xl lg:text-5xl sm:text-5xl text-4xl text-center lg:text-left font-semibold leading-tight mb-6">
                Skill Up While the
                <br />
                World Floats By
              </h1>
              <p className="xl:text-xl lg:text-lg text-white/90 hidden lg:block leading-relaxed max-w-lg">
                SMY's learning portal lets Indian mariners study anytime,
                anywhere with online support from subject experts.
              </p>
            </div>
          </div>
        </div>
        {/* Right side: animated form frame */}
        <div className="z-10 flex flex-col items-center lg:items-end justify-center lg:justify-end w-full lg:w-fit h-full relative lg:py-16 lg:px-8">
          <div
            className={`relative h-full w-[90%] sm:w-96 lg:w-[28rem] border-2 rounded-4xl p-8 shadow-2xl lg:ml-8 bg-transparent ${!reduceWidth ? "border-[var(--primary-font)]" : "border-white"} transition-all duration-500`}
          >
            {/* Animated label */}
            <div
              className={`tracking-[0.1rem] text-[var(--primary-font)] text-xs px-4 py-2 rounded-full mb-4 absolute -top-3 left-7 bg-white transition-opacity duration-500 ${!reduceWidth ? "opacity-0" : "opacity-100"}`}
              style={{ transitionDelay: isSignup ? "0ms" : "150ms" }}
            >
              LOGIN
            </div>
            <div
              className={`tracking-[0.1rem] h-fit text-white text-xs px-4 py-2 rounded-full mb-4 absolute -top-3 left-7 bg-[var(--primary-font)] transition-opacity duration-500 ${!reduceWidth ? "opacity-100" : "opacity-0"}`}
              style={{ transitionDelay: isSignup ? "150ms" : "0ms" }}
            >
              SIGN UP
            </div>
            <div
              className={`auth-form-fade ${loginFade} ${loginFade === "hidden" ? "auth-form-hidden" : ""}`}
            >
              <LoginForm />
            </div>
            <div
              className={`auth-form-fade ${signupFade} ${signupFade === "hidden" ? "auth-form-hidden" : ""}`}
            >
              <SignupForm onSignupSuccess={handleSignupSuccess} />
            </div>
          </div>
        </div>
      </div>

      {/* Verification Modal */}
      {showVerificationModal && verificationToken && (
        <VerificationModal
          token={verificationToken}
          onClose={handleVerificationClose}
        />
      )}
    </div>
  );
};

export default AuthSwitcher;
